export const SUITS = ['hearts', 'diamonds', 'clubs', 'spades'] as const;
export const VALUES = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'] as const;

export type Suit = typeof SUITS[number];
export type CardValue = typeof VALUES[number];

export interface Card {
  id: string;
  suit: Suit;
  value: CardValue;
  rank: number;
}

/**
 * Game-kind-agnostic board cell (nkr1.1, per SECOND_GAME_KIND_FINDINGS.md
 * Gap 3). Everything the board engine (BoardGenerator gravity / refill /
 * modifier spawning) and the shared UI plumbing need from a piece, with
 * none of the poker-specific card identity. CardWithPosition (poker) and
 * LetterCellWithPosition (word-grid) are both BoardPiece specializations,
 * so the engine's board functions are generic over `P extends BoardPiece`.
 */
export interface BoardPiece {
  id: string;
  row: number;
  col: number;
  isNew?: boolean;
  isClearing?: boolean;
  isFalling?: boolean;
  fallDistance?: number;
  isDroppingIn?: boolean;
  /**
   * Board modifier:
   * - 'golden' = match including it scores 2×; multi-golden stacks (2 = 4×, 3 = 8×)
   * - 'locked' = unselectable until `lockedFor` reaches 0; on unlock has 30% chance to upgrade to golden
   * - 'wild'   = counts as any suit/value during poker hand evaluation (rare, 1% spawn)
   */
  modifier?: 'golden' | 'locked' | 'wild' | null;
  /** Matches remaining before a locked piece is freed */
  lockedFor?: number;
  /** True for one match only, immediately after a locked piece upgrades to golden */
  justUnlocked?: boolean;
}

export interface CardWithPosition extends Card, BoardPiece {}

export interface Position {
  row: number;
  col: number;
}

export type HandName =
  | 'royal-flush'
  | 'straight-flush'
  | 'four-of-a-kind'
  | 'full-house'
  | 'flush'
  | 'straight'
  | 'three-of-a-kind'
  | 'two-pair'
  | 'pair'
  | 'high-card';

export interface HandResult {
  name: HandName;
  baseScore: number;
  cards: Card[];
}

export interface StreakState {
  streakActive: boolean;
  currentMultiplier: number;
  lastHandType: HandName | null;
  currentStackedHandType: HandName | null;
  alreadyStackedHands: Set<HandName>;
  streakTimer: number | null;
  /** Configurable per ruleset (timing.streakTimerMaxSec). Was the
   *  literal 15 pre-P1.2 when the value was hardcoded; widened to
   *  number once StreakManager became ruleset-driven. */
  readonly maxStreakTimer: number;
  isDecaying?: boolean;
  decayStartMultiplier?: number;
}

export interface StreakResult {
  multiplier: number;
  streakTimerVisible: boolean;
  streakTimerValue: number;
  timerAction: 'start' | 'reset' | 'continue' | 'none';
  streakStarted?: boolean;
  newHandStacked?: boolean;
  handRepeated?: boolean;
  bonusTimeAdded?: number;
}

export type GamePhase =
  | 'idle'
  | 'selecting'
  | 'evaluating'
  | 'scoring'
  | 'replacing'
  | 'paused'
  | 'ended';

export interface GameState {
  phase: GamePhase;
  board: CardWithPosition[];
  selectedCards: string[];
  score: number;
  handsPlayed: number;
  bestHand: HandName | null;
  highestMultiplier: number;
}

export interface GameStatistics {
  highestMultiplier: number;
  longestStreakTime: number;
  handCounts: Record<HandName, number>;
  mostPlayedHand: { hand: HandName; count: number } | null;
  highestHand: { hand: HandName; count: number } | null;
}

export const HAND_SCORES: Record<HandName, number> = {
  'royal-flush': 100,
  'straight-flush': 75,
  'four-of-a-kind': 50,
  'full-house': 25,
  'flush': 20,
  'straight': 15,
  'three-of-a-kind': 10,
  'two-pair': 5,
  'pair': 2,
  'high-card': 1,
};

export const HAND_NAMES: Record<HandName, string> = {
  'royal-flush': 'Royal Flush',
  'straight-flush': 'Straight Flush',
  'four-of-a-kind': 'Four of a Kind',
  'full-house': 'Full House',
  'flush': 'Flush',
  'straight': 'Straight',
  'three-of-a-kind': 'Three of a Kind',
  'two-pair': 'Two Pair',
  'pair': 'Pair',
  'high-card': 'High Card',
};

export const RANK_MAP: Record<CardValue, number> = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10,
  'J': 11, 'Q': 12, 'K': 13, 'A': 14
};

// Daily Challenge Types
export type ObjectiveType =
  | 'score'
  | 'pairs'
  | 'hands'
  | 'time'
  | 'specific-hands'
  | 'streaked-hands'
  | 'multiplier-target';

export interface HandRequirement {
  [handType: string]: number; // e.g., { "pair": 5, "full-house": 3 }
}

export interface MultiplierTarget {
  handType: HandName;
  targetMultiplier: number;
}

export interface DailyChallengeObjective {
  type: ObjectiveType;
  value: number;
  description: string;
  handRequirements?: HandRequirement;
  requireStreaked?: boolean;
  multiplierTarget?: MultiplierTarget;
}

export interface ChallengeProgress {
  regularHands: Record<HandName, number>;
  streakedHands: Record<HandName, number>;
  multiplierAchieved: {
    handType: HandName | null;
    multiplier: number;
  };
}
